import 'bootstrap/dist/css/bootstrap.css';
import { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload, faTimesCircle, faDownload, faRotate,
  faChevronLeft, faChevronRight, faRotateLeft, faFile, faHistory
} from '@fortawesome/free-solid-svg-icons';
import CustomAlert from '../../../shared/customAlert';
import Spinner from '../../../shared/spinner';
import RemessasService from '../../../services/importacaoAquisicaoService';
import PromotoraService from '../../../services/promotoraService';

// Tipos
interface Remessa {
  id: number;
  uuid: string;
  codigo: string;
  arquivo: string;
  nomeArquivo: string;
  layout: string;
  status: 'FINALIZADO' | 'ERRO' | 'PENDENTE' | 'PROCESSANDO' | 'DESFEITO';
  usuario: string;
  dtaCriacao: string;
  dtaProcessamento?: string;
  dtaFinalizacao?: string;
  contratosSucesso?: string;
  contratosErro?: string;
  totalContratos?: string;
}

interface LogOutroStatus {
  id: number;
  numeroContrato: string;
  status: string;
  descricao: string;
  dtaProcessamento?: string;
}

interface Layout {
  id: string;
  nome: string;
}

interface Alert {
  message: string;
  type: 'success' | 'error';
}

interface Promotora {
  id: string;
  nome: string;
}

interface ConfigAutomatica {
  id?: number;
  ativo: boolean;
  accessKeyS3: string;
  secretKeyS3: string;
  secretKeyConfigurada?: boolean;
  bucketS3: string;
  localizacaoS3: string;
  pastaCliente: string;
  pastaPromotoraBucket: string;
  tipoOrigem: 'PASTA' | 'ZIP';
  horaExecucao: string;
  idPromotora: string;
  nomePromotora?: string;
  dtaUltimaExecucao?: string;
  dtaUltimaTentativa?: string;
  ultimaDataImportada?: string;
  ultimoStatus?: string;
  ultimaMensagem?: string;
}

interface HistoricoImportacaoAutomatica {
  id: number;
  idConfig?: number;
  idRemessa?: number;
  codigoRemessa?: string;
  tipoExecucao?: string;
  status?: 'INICIADO' | 'SUCESSO' | 'ERRO' | 'IGNORADO';
  mensagem?: string;
  prefixo?: string;
  bucketS3?: string;
  pastaCliente?: string;
  pastaPromotoraBucket?: string;
  tipoOrigem?: string;
  idPromotora?: number;
  nomePromotora?: string;
  dataReferencia?: string;
  arquivoContratos?: string;
  arquivoParcelas?: string;
  arquivoZip?: string;
  totalObjetos?: number;
  dtaInicio?: string;
  dtaFim?: string;
  duracaoSegundos?: number;
}

const configAutomaticaInicial: ConfigAutomatica = {
  ativo: false,
  accessKeyS3: '',
  secretKeyS3: '',
  bucketS3: '',
  localizacaoS3: '',
  pastaCliente: '',
  pastaPromotoraBucket: '',
  tipoOrigem: 'PASTA',
  horaExecucao: '02:00',
  idPromotora: '',
};

// Componente
function RemessasComponent() {
  const [alert, setAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(false);
  const [remessas, setRemessas] = useState<Remessa[]>([]);
  const [modalUploadAberto, setModalUploadAberto] = useState(false);
  const [modalLogsAberto, setModalLogsAberto] = useState(false);
  const [logsOutrosStatus, setLogsOutrosStatus] = useState<LogOutroStatus[]>([]);
  const [remessaSelecionada, setRemessaSelecionada] = useState<Remessa | null>(null);
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [arquivoParcelasSelecionado, setArquivoParcelasSelecionado] = useState<File | null>(null);
  const [layoutSelecionado, setLayoutSelecionado] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [caminhoArquivoContratos, setCaminhoArquivoContratos] = useState('');
  const [caminhoArquivoParcelas, setCaminhoArquivoParcelas] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileParcelasInputRef = useRef<HTMLInputElement>(null);
  const [campoDescritivo, setCampoDescritivo] = useState('');
  const [abaAtiva, setAbaAtiva] = useState<'remessas' | 'automatica'>('remessas');
  const [configAutomatica, setConfigAutomatica] = useState<ConfigAutomatica>(configAutomaticaInicial);
  const [historicoAutomatico, setHistoricoAutomatico] = useState<HistoricoImportacaoAutomatica[]>([]);
  const [loadingHistoricoAutomatico, setLoadingHistoricoAutomatico] = useState(false);

  const remessasService = new RemessasService();
  const promotoraService = new PromotoraService();

  const layoutsDisponiveis: Layout[] = [
    { id: '1', nome: 'FIC3-OPEX' },
    { id: '2', nome: 'Padrão BPO' },
  ];

  const [promotoras, setPromotoras] = useState<Promotora[]>([]);
  const [promotoraSelecionada, setPromotoraSelecionada] = useState("");
  const layoutPadraoBpoSelecionado = layoutSelecionado === 'Padrão BPO';
  const promotoraObrigatoria = !layoutPadraoBpoSelecionado;
  const possuiArquivoContratos = Boolean(arquivoSelecionado || caminhoArquivoContratos.trim());
  const possuiArquivoParcelas = Boolean(arquivoParcelasSelecionado || caminhoArquivoParcelas.trim());
  const arquivosUploadValidos = layoutPadraoBpoSelecionado
    ? Boolean(possuiArquivoContratos && possuiArquivoParcelas)
    : Boolean(possuiArquivoContratos);
  const dadosUploadValidos = Boolean(
    arquivosUploadValidos
    && layoutSelecionado
    && (!promotoraObrigatoria || promotoraSelecionada)
  );
  const nomeArquivoContratos = arquivoSelecionado?.name || 'Nenhum arquivo selecionado';
  const nomeArquivoParcelas = arquivoParcelasSelecionado?.name || 'Nenhum arquivo selecionado';

  // ------------------- Funções -------------------

  const formatarData = (dataString?: string) => dataString ? new Date(dataString).toLocaleString('pt-BR') : '-';
  const formatarDuracao = (segundos?: number) => {
    if (segundos === undefined || segundos === null) return '-';
    if (segundos < 60) return `${segundos}s`;
    const minutos = Math.floor(segundos / 60);
    const restanteSegundos = segundos % 60;
    return `${minutos}min ${restanteSegundos}s`;
  };

  const badgeHistoricoAutomatico = (status?: string) => {
    if (status === 'SUCESSO') return 'bg-success';
    if (status === 'ERRO') return 'bg-danger';
    if (status === 'IGNORADO') return 'bg-warning text-dark';
    if (status === 'INICIADO') return 'bg-primary';
    return 'bg-secondary';
  };

  const arquivosHistoricoAutomatico = (historico: HistoricoImportacaoAutomatica) => {
    if (historico.arquivoZip) return historico.arquivoZip;
    return [historico.arquivoContratos, historico.arquivoParcelas].filter(Boolean).join(' | ') || '-';
  };

  const carregarRemessas = async (page = 0, size = 10) => {
    setLoading(true);
    try {
      const response = await remessasService.listar(page, size);
      setRemessas(response.content ?? []);
      setPaginaAtual(response.pageable.pageNumber ?? 0);
      setTotalPaginas(response.totalPages ?? 1);
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar remessas', type: 'error' });
      setRemessas([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarPromotorasAquisicao = async () => {
    try {
      const response = await promotoraService.listarTodasPromotorasAquisicao();
      setPromotoras(response.data ?? []);
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar promotoras de aquisição', type: 'error' });
    }
  };

  const carregarConfiguracaoAutomatica = async () => {
    setLoading(true);
    try {
      const response = await remessasService.buscarConfiguracaoAutomatica();
      setConfigAutomatica({
        ...configAutomaticaInicial,
        ...response,
        secretKeyS3: '',
        idPromotora: response?.idPromotora ? String(response.idPromotora) : '',
        tipoOrigem: response?.tipoOrigem === 'ZIP' ? 'ZIP' : 'PASTA',
        horaExecucao: response?.horaExecucao || '02:00',
      });
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar configuração automática', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const carregarHistoricoImportacaoAutomatica = async () => {
    setLoadingHistoricoAutomatico(true);
    try {
      const response = await remessasService.listarHistoricoImportacaoAutomatica(50);
      setHistoricoAutomatico(response ?? []);
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar histórico da importação automática', type: 'error' });
      setHistoricoAutomatico([]);
    } finally {
      setLoadingHistoricoAutomatico(false);
    }
  };

  useEffect(() => {
    carregarRemessas();
  }, []);

  useEffect(() => {
    if (modalUploadAberto || abaAtiva === 'automatica') {
      carregarPromotorasAquisicao();
    }
  }, [modalUploadAberto, abaAtiva]);

  useEffect(() => {
    if (abaAtiva === 'automatica') {
      carregarConfiguracaoAutomatica();
      carregarHistoricoImportacaoAutomatica();
    }
  }, [abaAtiva]);

  useEffect(() => {
    if (!layoutPadraoBpoSelecionado) {
      setArquivoParcelasSelecionado(null);
      setCaminhoArquivoParcelas('');
    }
  }, [layoutPadraoBpoSelecionado]);

  // ------------------- Upload -------------------
  const aplicarArquivosUpload = useCallback((files: File[]) => {
    if (files.length === 0) return;

    if (layoutPadraoBpoSelecionado) {
      const arquivoContratos = files.find(file => file.name.toUpperCase().includes('CTR')) ?? files[0];
      const arquivoParcelas = files.find(file => file.name.toUpperCase().includes('PAR'))
        ?? files.find(file => file !== arquivoContratos);

      setArquivoSelecionado(arquivoContratos);
      if (arquivoParcelas) {
        setArquivoParcelasSelecionado(arquivoParcelas);
      }
      return;
    }

    setArquivoSelecionado(files[0]);
  }, [layoutPadraoBpoSelecionado]);

  const selecionarArquivosContratos = useCallback((input: HTMLInputElement) => {
    const files = Array.from(input.files ?? []);
    if (files.length === 0) return;

    if (layoutPadraoBpoSelecionado && files.length > 1) {
      aplicarArquivosUpload(files);
      return;
    }

    setArquivoSelecionado(files[0]);
  }, [aplicarArquivosUpload, layoutPadraoBpoSelecionado]);

  const selecionarArquivoParcelas = useCallback((input: HTMLInputElement) => {
    const file = input.files?.[0];
    if (file) setArquivoParcelasSelecionado(file);
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    selecionarArquivosContratos(event.currentTarget);
  }, [selecionarArquivosContratos]);

  const handleFileInput = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    selecionarArquivosContratos(event.currentTarget);
  }, [selecionarArquivosContratos]);

  const handleFileParcelasChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    selecionarArquivoParcelas(event.currentTarget);
  }, [selecionarArquivoParcelas]);

  const handleFileParcelasInput = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    selecionarArquivoParcelas(event.currentTarget);
  }, [selecionarArquivoParcelas]);

  const handleDragOverUpload = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeaveUpload = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDropUpload = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    aplicarArquivosUpload(Array.from(event.dataTransfer.files));
  }, [aplicarArquivosUpload]);

  const abrirSeletorArquivos = useCallback(async (
    fallbackInputRef: React.RefObject<HTMLInputElement>,
    aoSelecionar: (files: File[]) => void,
    multiple = false,
  ) => {
    const showOpenFilePicker = (window as any).showOpenFilePicker;

    if (typeof showOpenFilePicker === 'function') {
      try {
        const handles = await showOpenFilePicker({
          multiple,
          types: [{
            description: 'Arquivos CSV',
            accept: {
              'text/csv': ['.csv'],
              'text/plain': ['.txt', '.rem'],
            },
          }],
        });
        const files = await Promise.all(handles.map((handle: any) => handle.getFile()));
        aoSelecionar(files);
        return;
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        console.error('Erro ao abrir seletor de arquivos:', error);
      }
    }

    fallbackInputRef.current?.click();
  }, []);

  const escolherArquivoContratos = useCallback(() => {
    abrirSeletorArquivos(fileInputRef, aplicarArquivosUpload, layoutPadraoBpoSelecionado);
  }, [abrirSeletorArquivos, aplicarArquivosUpload, layoutPadraoBpoSelecionado]);

  const escolherArquivoParcelas = useCallback(() => {
    abrirSeletorArquivos(fileParcelasInputRef, files => {
      if (files[0]) setArquivoParcelasSelecionado(files[0]);
    }, false);
  }, [abrirSeletorArquivos]);

  const resetarUpload = useCallback(() => {
    setArquivoSelecionado(null);
    setArquivoParcelasSelecionado(null);
    setCaminhoArquivoContratos('');
    setCaminhoArquivoParcelas('');
    setLayoutSelecionado('');
    setPromotoraSelecionada('');
    setCampoDescritivo('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (fileParcelasInputRef.current) {
      fileParcelasInputRef.current.value = '';
    }
  }, []);

  const fecharModalUpload = useCallback(() => {
    setModalUploadAberto(false);
    resetarUpload();
  }, [resetarUpload]);

  const enviarArquivo = async () => {
    if (!dadosUploadValidos) {
      setAlert({
        message: layoutPadraoBpoSelecionado
          ? 'Selecione os arquivos e o layout antes de enviar.'
          : 'Selecione o arquivo, um layout e uma promotora antes de enviar.',
        type: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      if (arquivoSelecionado) {
        formData.append('file', arquivoSelecionado);
      }
      if (caminhoArquivoContratos.trim()) {
        formData.append('caminhoArquivoContratos', caminhoArquivoContratos.trim());
      }
      if (layoutPadraoBpoSelecionado && arquivoParcelasSelecionado) {
        formData.append('fileParcelas', arquivoParcelasSelecionado);
      }
      if (layoutPadraoBpoSelecionado && caminhoArquivoParcelas.trim()) {
        formData.append('caminhoArquivoParcelas', caminhoArquivoParcelas.trim());
      }
      formData.append('layout', layoutSelecionado);
      if (promotoraSelecionada) {
        formData.append('idPromotora', promotoraSelecionada);
      }
      formData.append('usuario', localStorage.getItem('nome') || 'USER_API');
      formData.append('codigo', campoDescritivo || '');

      await remessasService.upload(formData);
      await carregarRemessas(0);
      setAlert({ message: 'Arquivo enviado com sucesso!', type: 'success' });

      setModalUploadAberto(false);
      resetarUpload();
    } catch (error) {
      console.error(error);
      const mensagemErro = (error as any)?.response?.data || 'Erro ao enviar o arquivo.';
      setAlert({ message: mensagemErro, type: 'error' });
    } finally {
      setLoading(false);
    }
  };


  // ------------------- Download -------------------
  const baixarArquivo = async (uuid: string, nomeArquivo: string) => {
    setLoading(true);
    try {
      const blob = await remessasService.download(uuid);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setAlert({ message: `Download iniciado: ${nomeArquivo}`, type: 'success' });
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao baixar o arquivo.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Logs -------------------
  const verLogsOutrosStatus = async (remessa: Remessa) => {
    setLoading(true);
    try {
      const logs = await remessasService.listarLogsOutrosStatus(remessa.id);
      setRemessaSelecionada(remessa);
      setLogsOutrosStatus(logs);
      setModalLogsAberto(true);
    } catch (error) {
      console.error(error);
      setAlert({ message: 'Erro ao carregar logs de outros status.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const verLogsRemessa = async (idRemessa: number) => {
    setLoading(true);
    try {
      const response = await remessasService.baixarLogTxt(idRemessa);
      const blob = new Blob([response], { type: 'text/plain;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `detalhes_aquisicao_${idRemessa}.txt`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setAlert({ message: `Download do TXT da remessa ${idRemessa} iniciado com sucesso!`, type: 'success' });
    } catch (error) {
      console.error(error);
      setAlert({ message: `Erro ao processar logs da remessa ${idRemessa}.`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Desfazer -------------------
  const desfazerRemessa = async (idRemessa: number) => {
    if (!window.confirm('Deseja realmente desfazer esta remessa?')) return;

    setLoading(true);
    try {
      await remessasService.desfazer(idRemessa);
      setAlert({ message: `Remessa ${idRemessa} desfeita com sucesso!`, type: 'success' });
      carregarRemessas(paginaAtual);
    } catch (error) {
      console.error(error);
      setAlert({ message: `Erro ao desfazer a remessa ${idRemessa}.`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Importação Automática -------------------
  const atualizarConfigAutomatica = (campo: keyof ConfigAutomatica, valor: string | boolean) => {
    setConfigAutomatica(prev => ({ ...prev, [campo]: valor } as ConfigAutomatica));
  };

  const normalizarPastaBucket = (valor: string) => (
    valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
  );

  const selecionarPromotoraAutomatica = (idPromotora: string) => {
    const promotora = promotoras.find(p => String(p.id) === idPromotora);
    setConfigAutomatica(prev => ({
      ...prev,
      idPromotora,
      pastaPromotoraBucket: prev.pastaPromotoraBucket || (promotora ? normalizarPastaBucket(promotora.nome) : ''),
    }));
  };

  const montarPayloadConfigAutomatica = () => ({
    ...configAutomatica,
    idPromotora: configAutomatica.idPromotora ? Number(configAutomatica.idPromotora) : null,
    ativo: Boolean(configAutomatica.ativo),
  });

  const mensagemErroRequest = (error: any, mensagemPadrao: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string') return data;
    if (data?.mensagem) return data.mensagem;
    return mensagemPadrao;
  };

  const salvarConfiguracaoAutomatica = async () => {
    setLoading(true);
    try {
      const response = await remessasService.salvarConfiguracaoAutomatica(montarPayloadConfigAutomatica());
      setConfigAutomatica({
        ...configAutomaticaInicial,
        ...response,
        secretKeyS3: '',
        idPromotora: response?.idPromotora ? String(response.idPromotora) : '',
        tipoOrigem: response?.tipoOrigem === 'ZIP' ? 'ZIP' : 'PASTA',
        horaExecucao: response?.horaExecucao || '02:00',
      });
      setAlert({ message: 'Configuração automática salva com sucesso!', type: 'success' });
      return response;
    } catch (error) {
      console.error(error);
      setAlert({ message: mensagemErroRequest(error, 'Erro ao salvar configuração automática.'), type: 'error' });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const testarConexaoAutomatica = async () => {
    setLoading(true);
    try {
      const response = await remessasService.testarConexaoAutomatica(montarPayloadConfigAutomatica());
      setAlert({ message: response?.mensagem || 'Conexão testada com sucesso!', type: 'success' });
    } catch (error) {
      console.error(error);
      setAlert({ message: mensagemErroRequest(error, 'Erro ao testar conexão automática.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const executarImportacaoAutomatica = async () => {
    setLoading(true);
    try {
      const configSalva = await remessasService.salvarConfiguracaoAutomatica(montarPayloadConfigAutomatica());
      const response = await remessasService.executarImportacaoAutomatica(configSalva.id);
      setConfigAutomatica({
        ...configAutomaticaInicial,
        ...configSalva,
        secretKeyS3: '',
        idPromotora: configSalva?.idPromotora ? String(configSalva.idPromotora) : '',
        tipoOrigem: configSalva?.tipoOrigem === 'ZIP' ? 'ZIP' : 'PASTA',
        horaExecucao: configSalva?.horaExecucao || '02:00',
        dtaUltimaExecucao: new Date().toISOString(),
        ultimaDataImportada: response?.dataEncontrada || configSalva?.ultimaDataImportada,
        ultimoStatus: response?.sucesso ? 'SUCESSO' : 'ERRO',
        ultimaMensagem: response?.mensagem,
      });
      setAlert({ message: response?.mensagem || 'Importação automática executada!', type: 'success' });
      carregarRemessas(paginaAtual);
      await carregarHistoricoImportacaoAutomatica();
    } catch (error) {
      console.error(error);
      setAlert({ message: mensagemErroRequest(error, 'Erro ao executar importação automática.'), type: 'error' });
      await carregarHistoricoImportacaoAutomatica();
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Paginação -------------------
  const irParaPagina = (novaPagina: number) => {
    if (novaPagina >= 0 && novaPagina < totalPaginas) {
      carregarRemessas(novaPagina);
    }
  };

  // ------------------- Render -------------------
  return (
    <div className="container-fluid mt-3">
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}

      <div className="card">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Minhas Aquisições</h5>
          {abaAtiva === 'remessas' && (
            <button className="btn btn-primary" onClick={() => setModalUploadAberto(true)}>
              <FontAwesomeIcon icon={faUpload} className="me-2" />Nova Aquisição
            </button>
          )}
        </div>

        <ul className="nav nav-tabs px-3 pt-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${abaAtiva === 'remessas' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('remessas')}
            >
              Remessas
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${abaAtiva === 'automatica' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('automatica')}
            >
              Importação automática
            </button>
          </li>
        </ul>

        <div className="card-body">
          {abaAtiva === 'remessas' && (
            loading ? (
              <Spinner loading={loading} />
            ) : remessas.length === 0 ? (
              <div className="alert alert-info text-center">Nenhuma remessa encontrada</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover table-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Código</th>
                        <th>Nome do Arquivo</th>
                        <th>Layout</th>
                        <th>Status</th>
                        <th>Contratos Sucesso</th>
                        <th>Contratos Erro</th>
                        <th>Total Contratos</th>
                        <th>Data de Criação</th>
                        <th>Data de Processamento</th>
                        <th>Data de Finalização</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {remessas.map(remessa => (
                        <tr key={remessa.uuid}>
                          <td>{remessa.id}</td>
                          <td>{remessa.codigo}</td>
                          <td>{remessa.nomeArquivo}</td>
                          <td>{remessa.layout}</td>
                          <td>
                            <span className={`badge ${remessa.status === 'FINALIZADO' ? 'bg-success' :
                              remessa.status === 'ERRO' ? 'bg-danger' :
                                remessa.status === 'PROCESSANDO' ? 'bg-primary' : 'bg-secondary'
                              }`}>{remessa.status}</span>
                          </td>
                          <td>{remessa.contratosSucesso}</td>
                          <td>{remessa.contratosErro}</td>
                          <td>{remessa.totalContratos}</td>
                          <td>{formatarData(remessa.dtaCriacao)}</td>
                          <td>{formatarData(remessa.dtaProcessamento)}</td>
                          <td>{formatarData(remessa.dtaFinalizacao)}</td>
                          <td>
                            {remessa.status === 'FINALIZADO' && (
                              <button className="btn btn-sm btn-warning" onClick={() => desfazerRemessa(remessa.id)}>
                                <FontAwesomeIcon icon={faRotateLeft} /> Desfazer
                              </button>
                            )}
                            {remessa.status === 'FINALIZADO' && (
                              <button className="btn btn-sm btn-success ms-2" onClick={() => verLogsRemessa(remessa.id)}>
                                <FontAwesomeIcon icon={faFile} className="me-1" /> Detalhes
                              </button>
                            )}
                            <button className="btn btn-sm btn-success ms-2" onClick={() => verLogsOutrosStatus(remessa)}>
                              <FontAwesomeIcon icon={faFile} className="me-1" /> Histórico
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => irParaPagina(paginaAtual - 1)} disabled={paginaAtual === 0}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Anterior
                  </button>
                  <span>Página {paginaAtual + 1} de {totalPaginas}</span>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => irParaPagina(paginaAtual + 1)} disabled={paginaAtual + 1 >= totalPaginas}>
                    Próxima <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </>
            )
          )}

          {abaAtiva === 'automatica' && (
            loading ? (
              <Spinner loading={loading} />
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Configuração S3</h6>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="importacaoAutomaticaAtiva"
                      checked={configAutomatica.ativo}
                      onChange={e => atualizarConfigAutomatica('ativo', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="importacaoAutomaticaAtiva">
                      {configAutomatica.ativo ? 'Ligado' : 'Desligado'}
                    </label>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Access Key S3</label>
                    <input
                      type="text"
                      className="form-control"
                      value={configAutomatica.accessKeyS3}
                      onChange={e => atualizarConfigAutomatica('accessKeyS3', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Secret Key S3</label>
                    <input
                      type="password"
                      className="form-control"
                      value={configAutomatica.secretKeyS3}
                      onChange={e => atualizarConfigAutomatica('secretKeyS3', e.target.value)}
                      placeholder={configAutomatica.secretKeyConfigurada ? 'Secret Key já configurada' : ''}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Bucket</label>
                    <input
                      type="text"
                      className="form-control"
                      value={configAutomatica.bucketS3}
                      onChange={e => atualizarConfigAutomatica('bucketS3', e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Região</label>
                    <input
                      type="text"
                      className="form-control"
                      value={configAutomatica.localizacaoS3}
                      onChange={e => atualizarConfigAutomatica('localizacaoS3', e.target.value)}
                      placeholder="sa-east-1"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Pasta Principal</label>
                    <input
                      type="text"
                      className="form-control"
                      value={configAutomatica.pastaCliente}
                      onChange={e => atualizarConfigAutomatica('pastaCliente', e.target.value)}
                      placeholder="carga-historica"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Promotora de Aquisição <small className="text-muted">(opcional)</small>
                    </label>
                    <select
                      className="form-select"
                      value={configAutomatica.idPromotora}
                      onChange={e => selecionarPromotoraAutomatica(e.target.value)}
                    >
                      <option value="">Usar códigos da planilha</option>
                      {promotoras.map(p => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Pasta da Promotora</label>
                    <input
                      type="text"
                      className="form-control"
                      value={configAutomatica.pastaPromotoraBucket}
                      onChange={e => atualizarConfigAutomatica('pastaPromotoraBucket', e.target.value)}
                      placeholder="singular"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Tipo</label>
                    <select
                      className="form-select"
                      value={configAutomatica.tipoOrigem}
                      onChange={e => atualizarConfigAutomatica('tipoOrigem', e.target.value as 'PASTA' | 'ZIP')}
                    >
                      <option value="PASTA">Pasta</option>
                      <option value="ZIP">Zip</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Hora</label>
                    <input
                      type="time"
                      className="form-control"
                      value={configAutomatica.horaExecucao}
                      onChange={e => atualizarConfigAutomatica('horaExecucao', e.target.value)}
                    />
                  </div>
                </div>

                {configAutomatica.ultimoStatus && (
                  <div className="border-top mt-4 pt-3">
                    <div className="row g-2">
                      <div className="col-md-3">
                        <strong>Status:</strong> {configAutomatica.ultimoStatus}
                      </div>
                      <div className="col-md-3">
                        <strong>Última execução:</strong> {formatarData(configAutomatica.dtaUltimaExecucao)}
                      </div>
                      <div className="col-md-3">
                        <strong>Última data:</strong> {configAutomatica.ultimaDataImportada || '-'}
                      </div>
                      <div className="col-md-3">
                        <strong>Mensagem:</strong> {configAutomatica.ultimaMensagem || '-'}
                      </div>
                    </div>
                  </div>
                )}

                <div className="border-top mt-4 pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      <FontAwesomeIcon icon={faHistory} className="me-2" />
                      Histórico de execuções
                    </h6>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={carregarHistoricoImportacaoAutomatica}
                      disabled={loadingHistoricoAutomatico}
                    >
                      <FontAwesomeIcon icon={faRotate} className="me-1" />
                      Atualizar
                    </button>
                  </div>

                  {loadingHistoricoAutomatico ? (
                    <Spinner loading={loadingHistoricoAutomatico} />
                  ) : historicoAutomatico.length === 0 ? (
                    <div className="alert alert-info text-center mb-0">Nenhuma execução automática encontrada</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover table-sm align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Início</th>
                            <th>Fim</th>
                            <th>Status</th>
                            <th>Execução</th>
                            <th>Origem</th>
                            <th>Data</th>
                            <th>Remessa</th>
                            <th>Arquivos</th>
                            <th>Duração</th>
                            <th>Mensagem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historicoAutomatico.map(historico => (
                            <tr key={historico.id}>
                              <td>{formatarData(historico.dtaInicio)}</td>
                              <td>{formatarData(historico.dtaFim)}</td>
                              <td>
                                <span className={`badge ${badgeHistoricoAutomatico(historico.status)}`}>
                                  {historico.status || '-'}
                                </span>
                              </td>
                              <td>{historico.tipoExecucao || '-'}</td>
                              <td>
                                <div>{historico.prefixo || '-'}</div>
                                <small className="text-muted">
                                  {historico.nomePromotora || 'Usar códigos da planilha'}
                                </small>
                              </td>
                              <td>{historico.dataReferencia || '-'}</td>
                              <td>{historico.codigoRemessa || historico.idRemessa || '-'}</td>
                              <td title={arquivosHistoricoAutomatico(historico)}>
                                {arquivosHistoricoAutomatico(historico)}
                              </td>
                              <td>{formatarDuracao(historico.duracaoSegundos)}</td>
                              <td title={historico.mensagem || '-'}>
                                {historico.mensagem || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button className="btn btn-outline-primary" onClick={testarConexaoAutomatica} disabled={loading}>
                    <FontAwesomeIcon icon={faRotate} className="me-2" />Testar conexão
                  </button>
                  <button className="btn btn-secondary" onClick={salvarConfiguracaoAutomatica} disabled={loading}>
                    Salvar
                  </button>
                  <button className="btn btn-primary" onClick={executarImportacaoAutomatica} disabled={loading}>
                    <FontAwesomeIcon icon={faDownload} className="me-2" />Executar agora
                  </button>
                </div>
              </>
            )
          )}
        </div>
      </div>

      {/* Modal Upload */}
      {modalUploadAberto && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar Nova Aquisição</h5>
                <button type="button" className="btn-close" onClick={fecharModalUpload}></button>
              </div>
              <div className="modal-body">
                <div
                  className={`border rounded p-3 mb-3 text-center ${isDragging ? 'border-primary bg-light' : ''}`}
                  onDragOver={handleDragOverUpload}
                  onDragLeave={handleDragLeaveUpload}
                  onDrop={handleDropUpload}
                >
                  {layoutPadraoBpoSelecionado ? 'Solte aqui os arquivos CTR e PAR' : 'Solte aqui o arquivo'}
                </div>

                <label className="form-label">
                  {layoutPadraoBpoSelecionado ? 'Arquivo de Contratos (CTR)' : 'Arquivo'}
                </label>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={escolherArquivoContratos}
                  >
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    {layoutPadraoBpoSelecionado ? 'Selecionar CTR/PAR' : 'Selecionar arquivo'}
                  </button>
                  <span className={arquivoSelecionado ? 'text-success' : 'text-muted'}>
                    {nomeArquivoContratos}
                  </span>
                </div>
                <input
                  id="arquivoContratosInput"
                  type="file"
                  className="d-none"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  onInput={handleFileInput}
                  accept=".csv,.txt,.rem"
                  multiple={layoutPadraoBpoSelecionado}
                />
                <div className={`small mb-3 ${arquivoSelecionado ? 'text-success' : 'text-muted'}`}>
                  Contrato: {nomeArquivoContratos}
                </div>
                <label className="form-label">
                  Caminho local do contrato <small className="text-muted">(fallback)</small>
                </label>
                <input
                  type="text"
                  className="form-control mb-3"
                  value={caminhoArquivoContratos}
                  onChange={e => setCaminhoArquivoContratos(e.target.value)}
                  placeholder="/home/victor-hugo/Downloads/carga-historica/singular/20260807/singular__w000000__p001-de-001__CTR.csv"
                />

                {layoutPadraoBpoSelecionado && (
                  <>
                    <label className="form-label">Arquivo de Parcelas (PAR)</label>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={escolherArquivoParcelas}
                      >
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        Selecionar PAR
                      </button>
                      <span className={arquivoParcelasSelecionado ? 'text-success' : 'text-muted'}>
                        {nomeArquivoParcelas}
                      </span>
                    </div>
                    <input
                      id="arquivoParcelasInput"
                      type="file"
                      className="d-none"
                      ref={fileParcelasInputRef}
                      onChange={handleFileParcelasChange}
                      onInput={handleFileParcelasInput}
                      accept=".csv"
                    />
                    <div className={`small mb-3 ${arquivoParcelasSelecionado ? 'text-success' : 'text-muted'}`}>
                      Parcelas: {nomeArquivoParcelas}
                    </div>
                    <label className="form-label">
                      Caminho local das parcelas <small className="text-muted">(fallback)</small>
                    </label>
                    <input
                      type="text"
                      className="form-control mb-3"
                      value={caminhoArquivoParcelas}
                      onChange={e => setCaminhoArquivoParcelas(e.target.value)}
                      placeholder="/home/victor-hugo/Downloads/carga-historica/singular/20260807/singular__v20260807__p001-de-001__PAR.csv"
                    />
                  </>
                )}

                {/* Novo Select de Promotoras */}
                <label className="form-label">
                  Promotora de Aquisição
                  {layoutPadraoBpoSelecionado && <small className="text-muted"> (opcional)</small>}
                </label>
                <select
                  className="form-select mb-3"
                  value={promotoraSelecionada}
                  onChange={e => setPromotoraSelecionada(e.target.value)}
                >
                  <option value="">
                    {layoutPadraoBpoSelecionado ? 'Usar códigos da planilha' : 'Selecione a promotora'}
                  </option>
                  {promotoras.map(p => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>

                <label className="form-label">Layout</label>
                <select
                  className="form-select mb-3"
                  value={layoutSelecionado}
                  onChange={e => setLayoutSelecionado(e.target.value)}
                >
                  <option value="">Selecione o layout</option>
                  {layoutsDisponiveis.map(layout => (
                    <option key={layout.id} value={layout.nome}>{layout.nome}</option>
                  ))}
                </select>

                {/* Campo descritivo */}
                <label htmlFor="campoDescritivo" className="form-label">
                  Campo Descritivo <small className="text-muted">(opcional)</small>
                </label>
                <input
                  type="text"
                  id="campoDescritivo"
                  className="form-control mb-3"
                  value={campoDescritivo}
                  onChange={e => setCampoDescritivo(e.target.value)}
                  placeholder="Digite um texto de controle interno"
                  maxLength={50}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={fecharModalUpload}>
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={enviarArquivo}
                  disabled={loading || !dadosUploadValidos}
                >
                  <FontAwesomeIcon icon={faUpload} className="me-2" />Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Logs de outros status */}
      {modalLogsAberto && (
        <div className="modal show d-block" tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Histórico</h5>
                <button type="button" className="btn-close" onClick={() => setModalLogsAberto(false)}></button>
              </div>
              <div className="modal-body">
                {loading ? <Spinner loading={loading} /> :
                  <table className="table table-bordered table-hover table-sm">
                    <thead className="table-light">
                      <tr>
                        <th>Status</th>
                        <th>Descrição</th>
                        <th>Data de Processamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          id: -1,
                          status: 'CRIAÇÃO',
                          descricao: `Criado pelo usuário ${remessaSelecionada?.usuario ?? '-'}`,
                          dtaProcessamento: formatarData(remessaSelecionada?.dtaCriacao)
                        },
                        ...logsOutrosStatus
                      ].map(log => (
                        <tr key={log.id}>
                          <td>{log.status}</td>
                          <td>{log.descricao}</td>
                          <td>{log.dtaProcessamento ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
                {logsOutrosStatus.length === 0 && !loading && <div className="alert alert-info text-center">Nenhum Histórico encontrado</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalLogsAberto(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default RemessasComponent;
